import { tokens } from "@blurb/codex-react";
import { createStyles } from "@mantine/core";
import { GraphQL, GraphQLQueryData } from "@rpi/openapi-api";
import React from "react";
import { useParams } from "react-router-dom";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { RpiRouteLink } from "../../../hooks/useNavigateRoute";
import { createRpiDrawerView, RpiDrawerProps } from "../../../rpi-core/drawer/RpiDrawer";
import { RpaDrawer } from "../../../rpi-core/drawer/RpaDrawer";
import { RpaDrawerField } from "../../../rpi-core/drawer/RpaDrawerField";
import { dashboardUserRoleLabel } from "../../../utils/graphql.util";
import { shouldRetry } from "../../../utils/query.util";

const useStyles = createStyles({
  body: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[4],
    padding: `0 ${tokens.spacing[6]}`,
  },
});

export const DashboardUserView = createRpiDrawerView<Pick<RpiDrawerProps, "opened" | "onClose">>(
  ({ opened, onClose }) => {
    const { id: stringId } = useParams();
    const id = React.useMemo(() => stringId && Number.parseInt(stringId), []);

    React.useEffect(() => {
      if (!stringId) onClose();
    }, [stringId]);

    const { data, isLoading, error } = GraphQL.useDashboardUserQuery(
      { id: id || -1 },
      {
        enabled: !!stringId && opened,
        refetchOnMount: true,
        retry: shouldRetry(onClose),
      }
    );
    const dashboardUser = React.useMemo(() => data?.dashboardUser, [data]);

    return (
      <RpaDrawer title="User details" isLoading={isLoading} error={error} opened={opened} onClose={onClose}>
        {dashboardUser && <UserInfo dashboardUser={dashboardUser} />}
      </RpaDrawer>
    );
  }
);

interface UserInfoProps {
  dashboardUser: GraphQLQueryData<GraphQL.DashboardUserQuery>;
}

const UserInfo: React.FC<UserInfoProps> = ({ dashboardUser }) => {
  const { classes } = useStyles();
  const user = useAuthUser();

  return (
    <div className={classes.body}>
      <RpaDrawerField label="First Name">{dashboardUser.firstName}</RpaDrawerField>
      <RpaDrawerField label="Last Name">{dashboardUser.lastName}</RpaDrawerField>
      <RpaDrawerField label="Role">{dashboardUserRoleLabel(dashboardUser.role)}</RpaDrawerField>
      <RpaDrawerField label="Username">{dashboardUser.username}</RpaDrawerField>
      <RpaDrawerField data-testid="dashboard-user-email" label="Email">
        {dashboardUser.email}
      </RpaDrawerField>
      <RpaDrawerField label="Sub">{dashboardUser.sub}</RpaDrawerField>
      {user.is.SUPER_ADMIN && dashboardUser.customerPublicId && (
        <RpaDrawerField label="Organization">
          <RpiRouteLink variant="inherit" route={["customer", { publicId: dashboardUser.customerPublicId }]}>
            {dashboardUser.customerPublicId}
          </RpiRouteLink>
        </RpaDrawerField>
      )}
    </div>
  );
};
